import { useForm } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogTitle, Grid, Tab, Tabs } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { FormProvider, RHFSwitch, RHFTextField } from '../../components/hook-form';

import { useQueryClient } from '@tanstack/react-query';
import { DialogAnimate } from 'src/components/animate';
import { GeneralDatePicker, GeneralSelectMenu } from 'src/components/general';
import GeneralUpload from 'src/components/general/GeneralUpload';
import AppSectionListModal from 'src/components/modals/AppSectionListModal';
import { FEED_ITEM_LIST } from 'src/constants/queryKeys';
import useTabs from 'src/hooks/useTabs';
import ContentSelectionTableModal from 'src/sections/@dashboard/project/feedItems/ContentSelectionTableModal';
import { GamePlaylistTableModal, MediaPlaylistTableModal } from 'src/sections/@dashboard/project/list/modals';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { SkeletonForm } from '../../components/skeleton';
import { networkCreateFeedItem, networkUpdateFeedItem } from '../../network/feed';
import { getDefaultValues } from './defaultValues';
import useIsLargerThan from 'src/hooks/useIsLargerThan';

// ----------------------------------------------------------------------

const TAB_MENU_ITEMS = [
  {
    value: 'SECTION',
    label: 'بخش',
  },
  {
    value: 'GAME',
    label: 'تک بازی',
  },
  {
    value: 'MEDIA_PLAYLIST',
    label: 'فهرست پخش رسانه',
  },
  {
    value: 'GAME_PLAYLIST',
    label: ' فهرست بازی ها',
  },
];

// ----------------------------------------------------------------------

export default function FeedItemForm() {
  const { enqueueSnackbar } = useSnackbar();

  const { projectId, feedId } = useParams();

  const queryClient = useQueryClient();

  const isLargerThenLg = useIsLargerThan('lg');

  const { modal, closeModal } = useFormsModalStore();

  const feedItemDetails = modal?.data;
  const isEditMode = !!feedItemDetails;

  const [isLoading, setIsLoading] = useState(false);

  const [cssFeedItems, setCssFeedItems] = useState([]);
  const [selectedCssFeedItem, setSelectedCssFeedItem] = useState('');

  const [linkTypes, setLinkTypes] = useState([]);
  const [selectedLinkType, setSelectedLinkType] = useState('');

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const [contentTypes, setContentTypes] = useState([]);
  const [selectedContentType, setSelectedContentType] = useState('');

  const [isOpen, setIsOpen] = useState(false);
  const { currentTab, onChangeTab } = useTabs('SECTION');

  const [date, setDate] = useState(feedItemDetails?.FeedItemPublishDate ? new Date(feedItemDetails?.FeedItemPublishDate) : new Date());

  const { appInfo } = useAppInfoStore();

  // ----------------------------------------------------------------------
  const defaultValues = getDefaultValues(feedItemDetails, selectedLinkType);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      FeedID: +feedId,
      FeedItemCssCaptionID: selectedCssFeedItem,
      PublishDate: new Date(date).toISOString(),
      IsActive: values.IsActive ? 1 : 0,
      LinkTitle: selectedLinkType,
      TrasnsactionAmountTypeID: selectedAmountType,
      FeedItemCostAmount: +values?.FeedItemCostAmount,
      FeedItemContentType: selectedContentType,
      FeedItemContentID: +values?.FeedItemContentID,
    };

    try {
      setIsLoading(true);
      if (isEditMode) {
        const res = await networkUpdateFeedItem(feedItemDetails?.FeedItemID, feedItemDetails?.FeedItemFeedID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [FEED_ITEM_LIST, feedId] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateFeedItem(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [FEED_ITEM_LIST, feedId] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // ----------------------------------------------------------

  useEffect(() => {
    const cssFeedItemList = generateSelectMenuItems(appInfo.CssFeedItem, 'FeedItemCssCaptionTitle', 'FeedItemCssCaptionID');

    const linkTypeList = generateSelectMenuItems(appInfo.LinkType, 'Title', 'Value');

    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');

    const contentTypeList = [
      { title: 'مدیا', id: 1 },
      { title: 'بازی', id: 2 },
    ];

    setCssFeedItems(cssFeedItemList);
    setSelectedCssFeedItem(isEditMode ? feedItemDetails?.FeedItemCssCaptionID || cssFeedItemList[0]?.id : cssFeedItemList[0]?.id);

    setLinkTypes(linkTypeList);
    setSelectedLinkType(isEditMode ? feedItemDetails?.FeedItemLinkTitle || linkTypeList[0]?.id : linkTypeList[0]?.id);

    setAmountTypes(amountTypeList);
    setSelectedAmountType(isEditMode ? feedItemDetails?.FeedItemTrasnsactionAmountTypeID || amountTypeList[0]?.id : amountTypeList[0]?.id);

    setContentTypes(contentTypeList);
    setSelectedContentType(isEditMode ? feedItemDetails?.FeedItemContentType || contentTypeList[0]?.id : contentTypeList[0]?.id);
  }, []);

  // ----------------------------------------------------------

  const selectItemHandler = (item) => {
    setIsOpen(false);

    if (currentTab === 'SECTION') {
      setValue('Link', `/section/${item?.ApplicationSectionKeyOrder}`);
    } else if (currentTab === 'GAME') {
      setValue('Link', `https://game.baazigooshi.com/${item?.GameID}`);
      setValue('Subject', item?.GameName);
      setValue('ImageSrc', item?.GameLogo);
      setValue('ContentDate', item?.GameLastUpdate);
    } else if (currentTab === 'MEDIA_PLAYLIST') {
      setValue('Link', `channel?url=HUB-API.keylid.com/MediaHub/${item?.MediaGroupID}`);
    } else if (currentTab === 'GAME_PLAYLIST') {
      setValue('Link', `channel?url=HUB-API.keylid.com/GameHub/${item?.GameJoinApplicationPlayListID}`);
    }
  };

  const renderSelectionComponent = () => {
    const components = {
      SECTION: <AppSectionListModal selectAppSectionHandler={selectItemHandler} projectId={+projectId} />,
      GAME: <ContentSelectionTableModal selectContentHandler={selectItemHandler} />,
      MEDIA_PLAYLIST: <MediaPlaylistTableModal selectContentHandler={selectItemHandler} />,
      GAME_PLAYLIST: <GamePlaylistTableModal selectContentHandler={selectItemHandler} />,
    };

    return components?.[currentTab];
  };

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              {isLoading ? (
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                    width: '100%',
                  }}
                >
                  <SkeletonForm number={16} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 3,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField name="Subject" type="text" label="عنوان" />
                    <RHFTextField name="SubjectSpecial" type="text" label="زیر عنوان" />

                    <RHFTextField
                      name="Link"
                      type="text"
                      label="لینک"
                      InputProps={{
                        endAdornment: (
                          <Button variant="contained" color="inherit" onClick={() => setIsOpen(true)}>
                            انتخاب
                          </Button>
                        ),
                      }}
                    />

                    <GeneralUpload
                      id="feed_item_image"
                      name="ImageSrc"
                      label="آدرس تصویر"
                      afterChangeAction={(res) => {
                        setValue('ImageSrc', res);
                      }}
                    />

                    <RHFTextField name="ContentDate" label="تاریخ تولید محتوا" type="text" />
                    <GeneralDatePicker name="PublishDate" label="تاریخ انتشار" value={date} onChangeValue={setDate} />

                    <GeneralSelectMenu
                      label="عنوان لینک محتوا"
                      name="LinkTitle"
                      options={linkTypes}
                      value={selectedLinkType}
                      onChangeValue={setSelectedLinkType}
                    />

                    <RHFTextField name="OrderIndex" type="number" label="ترتیب نمایش" />

                    <GeneralSelectMenu
                      label="ظاهر نمایش"
                      name="FeedItemCssCaptionID"
                      options={cssFeedItems}
                      value={selectedCssFeedItem}
                      onChangeValue={setSelectedCssFeedItem}
                    />

                    <GeneralSelectMenu
                      label="نوع محتوا"
                      name="FeedItemContentType"
                      options={contentTypes}
                      value={selectedContentType}
                      onChangeValue={setSelectedContentType}
                    />

                    <RHFTextField name="FeedItemContentID" type="text" label="شناسه محتوا" />

                    <GeneralSelectMenu
                      label="نوع واحد پرداخت"
                      name="TrasnsactionAmountTypeID"
                      options={amountTypes}
                      value={selectedAmountType}
                      onChangeValue={setSelectedAmountType}
                    />

                    <RHFTextField name="FeedItemCostLable" type="text" label="عنوان هزینه" />
                    <RHFTextField name="FeedItemCostAmount" type="number" label="مقدار هزینه" />

                    <RHFTextField name="TotalViewOnStart" type="number" label="شروع نمایش از" />
                    <RHFTextField name="TotalClickOnStart" type="number" label="شروع کلیک از" />

                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="flex-start"
                      spacing={3}
                      alignItems="center"
                      sx={{ width: '100%' }}
                    >
                      <RHFSwitch name="IsActive" labelPlacement="start" label="آیا آیتم نمایش داده شود؟" />
                    </Box>
                  </Box>

                  <div className="flex items-center gap-4 mt-6">
                    <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                      ذخیره
                    </LoadingButton>
                    <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                      {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                    </LoadingButton>
                    <Button onClick={() => closeModal()} variant="contained" color="error">
                      انصراف
                    </Button>
                  </div>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </FormProvider>

      <DialogAnimate open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center' }}>انتخاب محتوا</DialogTitle>
        <div className="mt-6 pr-7 w-full min-h-16">
          <Tabs allowScrollButtonsMobile scrollButtons={isLargerThenLg ? 'auto' : false} value={currentTab} onChange={onChangeTab}>
            {TAB_MENU_ITEMS.map((tab) => (
              <Tab disableRipple key={tab.value} value={tab.value} icon={tab.icon} label={tab.label} />
            ))}
          </Tabs>
        </div>

        <div style={{ maxHeight: '80vh' }}>{renderSelectionComponent()}</div>
      </DialogAnimate>
    </>
  );
}
